import { Box, Link } from '@iheartjane/reefer';
import capitalize from 'lodash-es/capitalize';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';

import { useCustomerDispatch } from '@jane/monolith/customer/dispatch';
import { selectCustomer } from '@jane/monolith/customer/redux/customer';
import { getIdentification } from '@jane/monolith/customer/redux/identification';
import { useCustomerSelector } from '@jane/monolith/customer/selectors';
import { useAuth } from '@jane/monolith/hooks/useAuth/useAuth';
import { LogInModal } from '@jane/shared-ecomm/components';
import { useHeadlessApp } from '@jane/shared/providers';
import { postMessageToIframeParent } from '@jane/shared/util';

interface Props {
  children?: ReactElement;
  isRewards?: boolean;
  rewardsLabel?: string | null;
  specialLabel?: string | null;
}

export const LoginProtected = ({
  children,
  isRewards = false,
  rewardsLabel,
  specialLabel,
}: Props): ReactElement => {
  const {
    options: { externalAuth },
  } = useHeadlessApp();
  const [openModal, setOpenModal] = useState(false);
  const { authenticated } = useCustomerSelector(selectCustomer);
  const dispatch = useCustomerDispatch();
  const { apple, errors, google, login, register, resetError, resetPassword } =
    useAuth();

  const renderedSpecialLabel = `Sign in to see ${capitalize(
    specialLabel || 'discounts'
  )}`;

  const renderedRewardsLabel = `Sign in to see ${rewardsLabel || 'rewards'}`;

  // load/unload the user's document:
  useEffect(() => {
    if (authenticated) {
      dispatch(getIdentification());
    }
  }, [authenticated]);

  const handleClick = () => {
    if (externalAuth) {
      postMessageToIframeParent({
        messageType: 'authEvent',
        payload: {
          name: 'initiateExternalAuth',
          source: 'bag',
        },
      });
    } else {
      setOpenModal(true);
    }
  };

  if (authenticated && children) return children;

  return (
    <Box pt={8} pb={24} pl={2}>
      <Link onClick={() => handleClick()}>
        {isRewards ? renderedRewardsLabel : renderedSpecialLabel}
      </Link>
      {openModal && (
        <LogInModal
          errors={errors}
          location="cart"
          open={true}
          onAppleClick={apple.onClick}
          onGoogleClick={google.onClick}
          onRequestClose={() => {
            resetError();
            setOpenModal(false);
          }}
          onSignInSubmit={login}
          onCreateAccountSubmit={register}
          onForgotPasswordSubmit={resetPassword}
          showAppleLogin={apple.showLogin}
          showGoogleLogin={google.showLogin}
        />
      )}
    </Box>
  );
};
